import { BulkUpdateDigitalWalletsRequest } from "@/models/digital.wallet";
import popupService from "@/services/popupService";
import authStore from "@/stores/authStore";
import digitalWalletStore from "@/stores/digital.wallet.store";
import { Box, Step, StepButton, Grid } from "@mui/material";
import { autorun, reaction } from "mobx";
import { observer, useObserver } from "mobx-react";
import moment from "moment";
import { FC, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BlueButtonOutlinedStyled from "../styled/blue.button.outlined.styled";
import LightGreenButtonStyled from "../styled/light.green.button.styled";
import StepperComponentStyled from "../styled/stepper.component.styled";
import BulkWalletsUpdateStepFour from "./bulk.wallets.update.step.four";
import BulkWalletsUpdateStepOne from "./bulk.wallets.update.step.one";
import BulkWalletsUpdateStepThree from "./bulk.wallets.update.step.three";
import BulkWalletsUpdateStepTwo from "./bulk.wallets.update.step.two";
import "./bulk.wallets.update.stepper.css";
import { checkEditPermissionWithoutAlert } from "@/util";
import { PermissionName } from "@/models/user";

interface IBulkWalletsUpdateStepper {
    closeDialog: Function;
}

const BulkWalletsUpdateStepper: FC<IBulkWalletsUpdateStepper> = ({ closeDialog }: IBulkWalletsUpdateStepper) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState<number>(0);
    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});

    const isValidate = () => {
        var promise = new Promise(async function (resolve, reject) {
            const request = digitalWalletStore.bulkUpdateDigitalWalletsRequest;
            let msg = "";
            if (request.digitalWallets.length === 0) {
                resolve(false);
            } else if (activeStep === 0 && request.type === 0) {
                resolve(false);
            } else if (activeStep === 1 && request.type === 1 && !request.amount) {
                msg = t("digitalWallet.EnterAmountValidation");
                popupService.showWarning(msg);
                resolve(false);
            } else if (activeStep === 1 && request.type === 2 && (!request.amount || !request.expiredDate)) {
                if (!request.amount) {
                    msg = t("digitalWallet.EnterAmountValidation");
                    popupService.showWarning(msg);
                    resolve(false);
                } else if (!request.expiredDate) {
                    msg = t("digitalWallet.SelectExpireDateValidation");
                    popupService.showWarning(msg);
                    resolve(false);
                }
            } else if (activeStep === 1 && request.type === 3 && !request.expiredDate) {
                msg = t("digitalWallet.SelectExpireDateValidation");
                popupService.showWarning(msg);
                resolve(false);
            } else if (activeStep === 1 && (request.type === 1 || request.type === 2)
                && request.amount && request.amount < 0
                && !checkEditPermissionWithoutAlert(PermissionName.LoadingNegativeWalletAmount)) {
                msg = t("digitalWallet.loadingNegativeWalletAmount.permission.accessDenied");
                popupService.showWarning(msg);
                resolve(false);
            }
            else if (activeStep === 1 && (request.type === 1 || request.type === 2)) {
                if (
                    digitalWalletStore.currentSubsidyBalanceOfAllEmployees &&
                    digitalWalletStore.subsidyBalance &&
                    digitalWalletStore.subsidyUsedPercentNotification &&
                    request.amount
                ) {
                    const currentUsedPercent =
                        100 -
                        ((digitalWalletStore.subsidyBalance - request.amount) /
                            digitalWalletStore.currentSubsidyBalanceOfAllEmployees) *
                        100;
                    if (currentUsedPercent >= digitalWalletStore.subsidyUsedPercentNotification) {
                        msg = t("digitalWallet.SubsidizationLimitReach", {
                            SubsidizationLimitReachPct: digitalWalletStore.subsidyUsedPercentNotification,
                        });
                        await popupService.showWarning(msg);
                    }
                }

                let digitalWalletsList = digitalWalletStore.digitalWalletsStore;
                let digitalWalletsSelected = digitalWalletsList.filter((d) => request.digitalWallets.includes(d.id));
                let today = moment();
                let notValidWallets = digitalWalletsSelected.filter((d) => {
                    if (!d.lastLoadingDate) {
                        return null;
                    }

                    let lastLoadingDate = moment(d.lastLoadingDate);
                    return d.lastLoadingDate && authStore.walletLoadingMinDays > today.diff(lastLoadingDate, "days");
                });

                if (notValidWallets.length > 0) {
                    msg = t("digitalWallet.WalletLoadingMinDaysError");
                    popupService.showConfirm(msg).then(function (res) {
                        if (res.isConfirmed) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                } else {
                    resolve(true);
                }
            } else {
                resolve(true);
            }
        });
        return promise;
    };

    const handleNext = async () => {
        var isValid = await isValidate();
        if (!isValid) {
            return;
        }
        const request = digitalWalletStore.bulkUpdateDigitalWalletsRequest;
        if (activeStep === 0 && request.type === 3) {
            removeStep();
        } else if (activeStep === 0 && request.type < 3) {
            addStep();
        }
        let nextStep = activeStep + 1;
        setActiveStep(nextStep);
    };

    const handleStep = (step: number) => async () => {
        var isValid = await isValidate();
        if (!isValid) {
            return;
        }
        setActiveStep(step);
    };

    const handleBack = () => {
        let nextStep = activeStep - 1;
        setActiveStep(nextStep);
    };

    const handleUpdate = async () => {
        try {
            const request = digitalWalletStore.bulkUpdateDigitalWalletsRequest;
            var result = await digitalWalletStore.bulkUpdateDigitalWallets(request);
            if (result) {
                digitalWalletStore.setUpdateWalletsAfterUpdate(true);
                let msg = t("digitalWallet.DigitalWalletsUpdatedSuccessfully");
                popupService.showSuccess(msg).then(function () {
                    closeDialog();
                });
            }
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            //closeDialog();
        }
    };
    const [steps, setSteps] = useState<Array<{ key: string; label: string; component: any }>>([
        {
            key: "ChooseCurrentOperationType",
            label: "Choose current operation type",
            component: <BulkWalletsUpdateStepOne handleNext={handleNext}></BulkWalletsUpdateStepOne>,
        },
        {
            key: "BulkWalletsUpdate",
            label: "Bulk wallets update",
            component: <BulkWalletsUpdateStepTwo></BulkWalletsUpdateStepTwo>,
        },
        {
            key: "DoYouWantToResetTheBalanceInAllSelectedWallets",
            label: "Do you want to reset the balance in all selected wallets?",
            component: <BulkWalletsUpdateStepThree></BulkWalletsUpdateStepThree>,
        },
        {
            key: "AreYouSureToUpdateWwallets",
            label: "Are you sure to update wallets?",
            component: <BulkWalletsUpdateStepFour></BulkWalletsUpdateStepFour>,
        },
    ]);

    const removeStep = () => {
        let stepsNew = [...steps];
        stepsNew = stepsNew.filter((item, i) => i !== 2);
        setSteps(stepsNew);
    };
    const addStep = () => {
        let stepsNew = [...steps];
        setSteps(stepsNew);
    };

    return (
        <Grid sx={{ width: "100%" }} className="bulk-wallets-update-stepper">
            <StepperComponentStyled nonLinear activeStep={activeStep}>
                {steps.map((step, index) => (
                    <Step key={step.key} completed={completed[index]}>
                        <StepButton color="inherit" onClick={handleStep(index)}></StepButton>
                    </Step>
                ))}
            </StepperComponentStyled>
            <div>
                <Fragment>
                    <Grid>{steps[activeStep].component}</Grid>
                    <Box sx={{ display: "flex", flexDirection: "row", paddingTop: "30px", gap: "15px" }}>
                        <BlueButtonOutlinedStyled
                            color="inherit"
                            disabled={activeStep === 0}
                            style={{ display: activeStep === 0 ? "none" : "block" }}
                            onClick={handleBack}
                        >
                            {t("general.Back")}
                        </BlueButtonOutlinedStyled>

                        <LightGreenButtonStyled
                            onClick={handleNext}
                            disabled={activeStep === steps.length - 1}
                            style={{
                                display:
                                    ((activeStep === 0 || activeStep === 3) &&
                                        digitalWalletStore.bulkUpdateDigitalWalletsRequest.type < 3) ||
                                        ((activeStep === 0 || activeStep === 2) &&
                                            digitalWalletStore.bulkUpdateDigitalWalletsRequest.type === 3)
                                        ? "none"
                                        : "block",
                            }}
                        >
                            {activeStep === 2 ? t("general.Continue") : t("general.Next")}
                        </LightGreenButtonStyled>

                        <LightGreenButtonStyled
                            onClick={handleUpdate}
                            style={{
                                display:
                                    (activeStep === 3 && digitalWalletStore.bulkUpdateDigitalWalletsRequest.type < 3) ||
                                        (activeStep === 2 && digitalWalletStore.bulkUpdateDigitalWalletsRequest.type === 3)
                                        ? "block"
                                        : "none",
                            }}
                        >
                            {t("general.Update")}
                        </LightGreenButtonStyled>
                    </Box>
                </Fragment>
            </div>
        </Grid>
    );
};

export default BulkWalletsUpdateStepper;
